import { DataTable, DataTableSortEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { useRef, useState } from "react";
import { Button } from "primereact/button";
import QualityCheckForm from "./RawMaterialQualityCheck";
import { RawMaterialPurchaseListDTO } from "../../models/RawMaterials/RawMaterial-model";

interface DataTableProps {
  data: RawMaterialPurchaseListDTO[];
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  onPageChange: (event: any) => void;
  isEmptyState?: boolean;
  onUpdate: (material: RawMaterialPurchaseListDTO) => void;
  onSortChange: (field: string, order: 1 | -1) => void;
  qcStatuses?: any[];
}

const RawMaterialsDatatable: React.FC<DataTableProps> = (props) => {
  const [sortField, setSortField] = useState<string>("createdDate");
  const [sortOrder, setSortOrder] = useState<1 | -1>(1);
  const toast = useRef<Toast>(null);
  const [showQualityCheckForm, setShowQualityCheckForm] = useState(false);
  const [selectedRawMaterial, setSelectedRawMaterial] = useState<any>(null);

  const showMessage = (severity: "success" | "error" | "warn", summary: string, detail: string) => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  const onSort = (e: DataTableSortEvent) => {
    setSortField(e.sortField || "");
    setSortOrder(e.sortOrder as 1 | -1);
    props.onSortChange(e.sortField || "", e.sortOrder as 1 | -1);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const rupee = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  });

  const parseNumber = (val: any) => {
    if (val == null) return 0;
    if (typeof val === "number") return val;
    if (typeof val === "string") {
      const n = parseFloat(val.replace(/[^0-9.\-]/g, ""));
      return isNaN(n) ? 0 : n;
    }
    return 0;
  };

  const approxEqual = (a: number, b: number, eps = 1e-6) => Math.abs(a - b) < eps;

  const qualityCheckButtonTemplate = (rowData: any) => {
    const invoiced = parseNumber(rowData.quantity ?? rowData.quantityWithUnit);
    const accepted = parseNumber(rowData.acceptedQuantity ?? rowData.acceptedQuantityWithUnit);
    const rejected = parseNumber(rowData.rejectedQuantity ?? rowData.rejectedQuantityWithUnit);

    const remainingFromApi = parseNumber(
      rowData.remainingQuantity ?? rowData.remainingQuantityWithUnit
    );
    const sumAR = accepted + rejected;
    const computedRemaining = Math.max(0, invoiced - sumAR);
    const remaining = remainingFromApi > 0 ? remainingFromApi : computedRemaining;

    // Default values
    let statusName = "QC Pending";
    let className = "p-button-warning p-button-sm";
    let disabled = false;

    // === Determine QC Status purely by name ===
    if (approxEqual(invoiced, accepted) && invoiced > 0) {
      statusName = "Accepted";
      className = "p-button-success p-button-sm";
      disabled = true;
    } else if (approxEqual(invoiced, rejected) && invoiced > 0) {
      statusName = "Rejected";
      className = "p-button-danger p-button-sm";
      disabled = true;
    } else if (approxEqual(invoiced, sumAR) && invoiced > 0) {
      statusName = "Accepted & Rejected";
      className = "p-button-help p-button-sm";
      disabled = true;
    } else if (approxEqual(invoiced, remaining) && invoiced > 0) {
      statusName = "QC Pending";
      className = "p-button-warning p-button-sm";
      disabled = false;
    } else if (approxEqual(accepted, 0) && rejected > 0 && !approxEqual(rejected, invoiced)) {
      statusName = "Partially Rejected";
      className = "p-button-danger p-button-sm";
      disabled = false;
    } else {
      statusName = "Partially Accepted";
      className = "p-button-info p-button-sm";
      disabled = false;
    }

    // ✅ Match by name only (no ID usage anywhere)
    const qcStatus = props.qcStatuses?.find(
      (s: any) => s.name?.toLowerCase().trim() === statusName.toLowerCase().trim()
    );
    const finalLabel = qcStatus?.name || statusName;

    const handleClick = () => {
      if (disabled) return;
      setSelectedRawMaterial({
        ...rowData,
        invoicedQuantity: invoiced,
        acceptedQuantityNum: accepted,
        rejectedQuantityNum: rejected,
        remainingQuantity: remaining,
        qcStatusName: finalLabel, // send name, not ID
      });
      setShowQualityCheckForm(true);
    };

    return (
      <Button
        label={finalLabel}
        className={className}
        disabled={disabled}
        onClick={handleClick}
        style={{
          fontSize: "0.75rem",
          padding: "4px 8px",
          height: "32px",
          width: "160px",
          color: "white",
          opacity: disabled ? 0.7 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      />
    );
  };

  const handleQualityCheckSave = (updatedMaterial: any) => {
    props.onUpdate({
      ...updatedMaterial,
      qcStatusName: updatedMaterial.qcStatusName,
    });
    setShowQualityCheckForm(false);
    showMessage("success", "Success", "Quality check completed successfully");
  };

  return (
    <div className="col-12">
      <Toast ref={toast} />
      <div className="card border-noround p-2">
        <DataTable
          value={props.data}
          paginator
          rows={props.pageSize}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          totalRecords={props.totalRecords}
          lazy
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate={
            props.isEmptyState
              ? "{totalRecords} materials"
              : "{first} to {last} of {totalRecords}"
          }
          first={(props.pageNumber - 1) * props.pageSize}
          onPage={props.onPageChange}
          sortMode="single"
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={onSort}
          editMode="row"
        >
          <Column
            field="purchaseDate"
            header="Purchase Date"
            style={{ width: "10%" }}
            sortable
            body={(rowData) => formatDate(rowData.purchaseDate)}
          />
          <Column
            header="Actions"
            body={qualityCheckButtonTemplate}
            style={{ textAlign: "center", width: "140px" }}
          />
          <Column field="rawMaterialName" header="Raw Material" style={{ width: "10%" }} sortable />
          <Column field="categoryName" header="Category" style={{ width: "15%" }} sortable />
          <Column field="invoiceId" header="Invoice No." style={{ width: "12%" }} sortable />
          <Column field="vendorName" header="Vendor" style={{ width: "15%" }} sortable />
          <Column
            field="quantityWithUnit"
            header="Invoiced Qty"
            style={{ width: "10%" }}
            sortable
          />
          <Column
            field="acceptedQuantity"
            header="Accepted Qty"
            body={(rowData) => rowData.acceptedQuantityWithUnit}
            style={{ width: "10%" }}
            sortable
          />
          <Column
            field="remainingQuantity"
            header="Remaining Qty"
            body={(rowData) => rowData.remainingQuantityWithUnit}
            style={{ width: "10%" }}
            sortable
          />
          <Column
            field="rejectedQuantity"
            header="Rejected Qty"
            body={(rowData) => rowData.rejectedQuantityWithUnit}
            style={{ width: "10%" }}
            sortable
          />
          <Column
            field="basicAmount"
            header="Base Price"
            style={{ width: "10%" }}
            body={(rowData) => rupee.format(parseNumber(rowData.basicAmount))}
            sortable
          />
          <Column
            field="taxAmount"
            header="Tax Amount"
            style={{ width: "10%" }}
            body={(rowData) => rupee.format(parseNumber(rowData.taxAmount))}
            sortable
          />
          <Column
            field="totalAmount"
            header="Total Amount"
            style={{ width: "10%" }}
            body={(rowData) => rupee.format(parseNumber(rowData.totalAmount))}
            sortable
          />
          <Column field="updatedByName" header="QC By" style={{ width: "10%" }} sortable />
          <Column
            field="updatedDate"
            header="QC Date"
            body={(rowData) => formatDate(rowData.updatedDate)}
            style={{ width: "10%" }}
            sortable
          />
        </DataTable>

        <QualityCheckForm
          visible={showQualityCheckForm}
          onHide={() => setShowQualityCheckForm(false)}
          rawMaterial={selectedRawMaterial}
          onSaveSuccess={handleQualityCheckSave}
        />
      </div>
    </div>
  );
};

export default RawMaterialsDatatable;
